import { Dialog } from '@headlessui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ErrorDisplay from './ErrorDisplay';

export default function BranchFormModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialValues = null, 
  companies = [], 
  userRole,
  companyId = null
}) {
  const isEditing = !!initialValues;
  const isAdmin = userRole === 'admin';
  const { t } = useLanguage();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getInitialValues = () => {
    if (initialValues) {
      return initialValues;
    }
    
    const defaultValues = {
      name: '',
      email: '',
      companyId: companyId || '',
      phone: '',
      address: '',
      isActive: true,
      receiveMonthlyReports: false,
      reportEmails: '',
      reportScheduleDay: 1,
      reportScheduleTime: '08:00',
      reportTimezone: 'Africa/Tripoli',
      branchManagerId: null
    };
    
    return defaultValues;
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t('forms.required')),
    email: Yup.string().email(t('forms.invalidEmail')).required(t('forms.required')),
    companyId: isAdmin ? Yup.number().required(t('forms.required')) : Yup.number(),
    phone: Yup.string().nullable(),
    address: Yup.string().nullable(),
    isActive: Yup.boolean(),
    receiveMonthlyReports: Yup.boolean(),
    reportEmails: Yup.string().nullable(),
    reportScheduleDay: Yup.number().min(1).max(31).nullable(),
    reportScheduleTime: Yup.string().nullable(),
    reportTimezone: Yup.string().nullable(),
    branchManagerId: Yup.number().nullable()
  });

  // Clear error when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      await onSubmit(values);
      setError(null);
      handleClose();
    } catch (submitError) {
      console.error('Form submission error:', submitError);
      setError(submitError);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-2xl w-full rounded bg-white p-6 my-8">
          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {isEditing ? t('branches.editBranch') : t('branches.addBranch')}
          </Dialog.Title>

          {error && (
            <ErrorDisplay 
              error={error} 
              onDismiss={() => setError(null)} 
              className="mb-6"
            />
          )}
          
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t('branches.branchName')}
                  </label>
                  <Field
                    id="name"
                    name="name"
                    className={`mt-1 input ${errors.name && touched.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && touched.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('branches.email')}
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    className={`mt-1 input ${errors.email && touched.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && touched.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                </div>

                {isAdmin && (
                  <div>
                    <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                      {t('branches.company')}
                    </label>
                    <Field
                      as="select"
                      id="companyId"
                      name="companyId"
                      className={`mt-1 input ${errors.companyId && touched.companyId ? 'border-red-500' : ''}`}
                    >
                      <option value="">{t('branches.selectCompany')}</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Field>
                    {errors.companyId && touched.companyId && (
                      <p className="mt-1 text-xs text-red-500">{errors.companyId}</p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    {t('branches.phone')}
                  </label>
                  <Field
                    id="phone"
                    name="phone"
                    className="mt-1 input"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    {t('branches.address')}
                  </label>
                  <Field
                    id="address"
                    name="address"
                    as="textarea"
                    rows={3}
                    className="mt-1 input"
                  />
                </div>

                <div className="flex items-center">
                  <Field 
                    id="isActive" 
                    name="isActive" 
                    type="checkbox" 
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500" 
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    {t('branches.activeBranch')}
                  </label>
                </div>

                {/* Monthly Report Settings */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">
                    📧 {t('branches.monthlyReportSettings')}
                  </h3>
                  
                  <div className="flex items-center mb-3">
                    <Field 
                      id="receiveMonthlyReports" 
                      name="receiveMonthlyReports" 
                      type="checkbox" 
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500" 
                    />
                    <label htmlFor="receiveMonthlyReports" className="ml-2 block text-sm text-gray-900">
                      {t('branches.receiveMonthlyReports')}
                    </label>
                  </div>

                  <div>
                    <label htmlFor="reportEmails" className="block text-sm font-medium text-gray-700">
                      {t('branches.reportEmails')}
                    </label>
                    <Field
                      id="reportEmails"
                      name="reportEmails"
                      placeholder="email1@branch.com, email2@branch.com"
                      className="mt-1 input"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {t('branches.reportEmailsHelp')}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div>
                      <label htmlFor="reportScheduleDay" className="block text-sm font-medium text-gray-700">
                        {t('branches.scheduleDay')}
                      </label>
                      <Field
                        as="select"
                        id="reportScheduleDay"
                        name="reportScheduleDay"
                        className="mt-1 input"
                      >
                        {[...Array(31)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </Field>
                    </div>

                    <div>
                      <label htmlFor="reportScheduleTime" className="block text-sm font-medium text-gray-700">
                        {t('branches.scheduleTime')}
                      </label>
                      <Field
                        type="time"
                        id="reportScheduleTime"
                        name="reportScheduleTime"
                        className="mt-1 input"
                      />
                    </div>

                    <div>
                      <label htmlFor="reportTimezone" className="block text-sm font-medium text-gray-700">
                        {t('branches.timezone')}
                      </label>
                      <Field
                        as="select"
                        id="reportTimezone"
                        name="reportTimezone"
                        className="mt-1 input"
                      >
                        <option value="Africa/Tripoli">Libya (Tripoli)</option>
                        <option value="Africa/Cairo">Egypt (Cairo)</option>
                        <option value="Europe/London">UK (London)</option>
                        <option value="America/New_York">US (New York)</option>
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditing ? t('common.updating') : t('common.creating')}...
                      </>
                    ) : (
                      isEditing ? t('common.update') : t('common.create')
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
} 